<script lang="ts" setup>
import type { Configuration } from '~/types/configurator'

const { t } = useI18n()

const model = defineModel<boolean>()

const props = defineProps<{
  origin: {
    accessoriesOption: Configuration['accessoriesOptions'][number]
    accessory: Configuration['accessoriesOptions'][number]['values'][number]
  }
  accessories: {
    addedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      accessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[]
    removedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      accessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[]
    swappedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      oldAccessory: Configuration['accessoriesOptions'][number]['values'][number]
      newAccessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[]
  }
}>()

const keys = [
  'addedAccessories',
  'swappedAccessories',
  'removedAccessories',
] as const
type AccessoriesKeys = (typeof keys)[number]

const icons: Record<AccessoriesKeys, string> = {
  addedAccessories: 'fa6-solid:plus',
  swappedAccessories: 'fa6-solid:arrow-right-arrow-left',
  removedAccessories: 'fa6-solid:minus',
}

const titles: Record<AccessoriesKeys, string> = {
  addedAccessories: t('configurator.accessoryChange.addedAccessories'),
  swappedAccessories: t('configurator.accessoryChange.swappedAccessories'),
  removedAccessories: t('configurator.accessoryChange.removedAccessories'),
}

const emit = defineEmits(['close'])
</script>

<template>
  <UiModal
    v-model="model"
    panel-classes="bg-white max-w-md"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-2">
      <p class="text-pragmatica-extended mb-2 text-4xl font-bold">
        {{ t('configurator.accessoryChange.title') }}
      </p>
      <div class="flex items-center gap-2">
        <Icon
          name="f7:exclamationmark-circle"
          size="20"
          class="mt-0.5 min-w-4"
        />
        <p>{{ t('configurator.accessoryChange.basedOnSelection') }}:</p>
      </div>
      <ProductConfiguratorAccessoriesChangeBlock
        :origin="props.origin"
        :accessories="[props.origin]"
      />
      <template v-for="key in keys">
        <template v-if="props.accessories[key].length">
          <div class="mt-4 flex gap-2">
            <Icon :name="icons[key]" size="18" class="mt-0.5 min-w-4" />
            <p class="font-semibold">{{ titles[key] }}:</p>
          </div>
          <ProductConfiguratorAccessoriesChangeBlock
            :origin="props.origin"
            :accessories="props.accessories[key]"
          />
        </template>
      </template>
      <button
        class="text-pragmatica-extended mx-auto mt-4 w-fit rounded-2xl bg-black px-3 py-2 font-bold text-white"
        @click="emit('close')"
      >
        {{ t('configurator.accessoryChange.understood') }}
      </button>
    </div>
  </UiModal>
</template>
